import { NavLink, Outlet } from 'react-router-dom'
import { CalendarDays, Car, LogOut, ShoppingBag, UserRound } from 'lucide-react'
import PageIntro from '../components/ui/PageIntro'
import Section from '../components/ui/Section'
import { StatCard } from '../components/account/AccountUi'
import { useAccount } from '../hooks/account-context'
import { useAccountQuery } from '../hooks/useAccountQueries'

/* The buyer's own corner of the site.

   One masthead, the four figures somebody signs in to check, and a row of
   tabs over whichever list they came for. The figures live up here rather
   than on the overview tab because they are the answer for most visits: what
   is outstanding, and when is the next month due. Anybody who wants the
   detail is one tab away.

   The tabs are routes rather than local state so a link to somebody's
   instalments is a link somebody can send. */

const TABS = [
  { to: '/account', label: 'Overview', icon: UserRound, end: true },
  { to: '/account/bookings', label: 'Bookings', icon: ShoppingBag },
  { to: '/account/instalments', label: 'Instalments', icon: CalendarDays },
  { to: '/account/cars', label: 'My cars', icon: Car },
]

export function Account() {
  const { session, signOut } = useAccount()
  const query = useAccountQuery()

  const summary = query.data?.summary
  const name = query.data?.customer?.fullName || session?.user.fullName || 'there'

  return (
    <>
      <PageIntro
        eyebrow="Your account"
        title={`Hello, ${name.split(' ')[0]}`}
        ghost="Account"
        lede="Everything you have with us: your orders and what has been paid against them, the months on any bank plan, and the cars now in your name."
      />

      <Section className="!pt-12 sm:!pt-14">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard
            label="Orders"
            value={summary?.bookings ?? '—'}
            hint={summary ? `${summary.open} still open` : undefined}
          />
          <StatCard
            label="Cars in your name"
            value={summary?.cars ?? '—'}
            hint={summary && summary.cars > 0 ? 'Registered to you' : 'None yet'}
          />
          <StatCard
            label="Bank plans"
            value={summary?.plans ?? '—'}
            hint={summary && summary.plans > 0 ? 'See the calendar' : 'No plan running'}
          />
          <StatCard
            label="Signed in as"
            value={session?.user.username ?? '—'}
            hint={session?.user.roleName}
          />
        </div>

        {/* The tabs. They scroll rather than wrap on a phone: four items that
            wrap onto two lines read as two rows of navigation. */}
        <nav
          aria-label="Your account"
          className="mt-10 -mx-5 flex gap-1.5 overflow-x-auto px-5 pb-1 sm:mx-0 sm:px-0"
        >
          {TABS.map((tab) => (
            <NavLink
              key={tab.to}
              to={tab.to}
              end={tab.end}
              className={({ isActive }) =>
                `flex shrink-0 items-center gap-2 rounded-full border px-4 py-2.5 text-[13.5px] font-medium transition duration-200 ${
                  isActive
                    ? 'border-lime/55 bg-lime/10 text-lime'
                    : 'border-line bg-raised/50 text-steel hover:border-lime/40 hover:text-platinum'
                }`
              }
            >
              <tab.icon aria-hidden="true" className="size-4" strokeWidth={1.9} />
              {tab.label}
            </NavLink>
          ))}

          <button
            type="button"
            onClick={signOut}
            className="ml-auto flex shrink-0 items-center gap-2 rounded-full border border-line bg-raised/50 px-4 py-2.5 text-[13.5px] font-medium text-steel transition duration-200 hover:border-red-500/40 hover:text-red-200"
          >
            <LogOut aria-hidden="true" className="size-4" strokeWidth={1.9} />
            Sign out
          </button>
        </nav>

        <div className="mt-6">
          <Outlet />
        </div>
      </Section>
    </>
  )
}

export default Account
